import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

// Navigation for the host dashboard. Sections mirror how hosts work:
// day-to-day (bookings, messages), their listings, then the business side.

const SECTIONS = [
  {
    title: null,
    items: [
      { to: "/app/dashboard", icon: "fas fa-th-large", label: "Overview" },
      { to: "/app/bookings", icon: "far fa-calendar-check", label: "Bookings" },
      { to: "/app/messages", icon: "far fa-comment-dots", label: "Messages", badge: "messages" },
      { to: "/app/homes/calendar", icon: "far fa-calendar-alt", label: "Calendar" },
    ],
  },
  {
    title: "Listings",
    items: [
      { to: "/app/homes", icon: "fas fa-home", label: "Homes", exact: true },
      { to: "/app/properties", icon: "fas fa-hotel", label: "Hotels" },
      { to: "/app/experiences", icon: "fas fa-compass", label: "Experiences" },
      { to: "/app/events", icon: "fas fa-ticket-alt", label: "Events" },
    ],
  },
  {
    title: "Business",
    items: [
      { to: "/app/tickets", icon: "fas fa-qrcode", label: "Ticket check-in" },
      { to: "/app/customers", icon: "fas fa-user-friends", label: "Customers" },
      { to: "/app/transactions", icon: "fas fa-exchange-alt", label: "Transactions" },
      { to: "/app/workforce", icon: "fas fa-robot", label: "AI Workforce" },
      { to: "/app/calendarSync", icon: "fas fa-sync-alt", label: "Calendar sync" },
    ],
  },
];

const NEW_LISTINGS = [
  { to: "/app/createHome", icon: "fas fa-home", label: "Home" },
  { to: "/app/createHotel", icon: "fas fa-hotel", label: "Hotel" },
  { to: "/app/createExperience", icon: "fas fa-compass", label: "Experience" },
  { to: "/app/createEvent", icon: "fas fa-ticket-alt", label: "Event" },
];

const DashboardSidebar = ({ onMobileClose = () => {}, openMobile = false, unreadMessages = 0 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const host = useSelector((state) => state.edit_host?.host);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (openMobile && onMobileClose) onMobileClose();
    setCreating(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const profile = host?.User_ID;
  const profileComplete = Boolean(profile?.first_name && profile?.last_name && profile?.phone_number);

  const startListing = (to) => {
    if (!profileComplete) {
      toast.info("Add your name and phone number first, so guests know who they're booking with.");
      navigate("/app/account");
      return;
    }
    navigate(to);
  };

  const logout = () => {
    localStorage.clear();
    window.location.replace("/");
  };

  return (
    <>
      <div className={`hd-sidebar-backdrop ${openMobile ? "open" : ""}`} onClick={onMobileClose} />
      <aside className={`hd-sidebar ${openMobile ? "open" : ""}`}>
        <div className="hd-sidebar-brand">
          <img src="/static/images/tubayo-logo-dark.png" alt="" width={34} height={34} />
          <span>
            Tubayo <small>Host</small>
          </span>
        </div>

        <div className="hd-new-listing">
          <button type="button" className="hd-btn hd-btn-primary hd-btn-block" onClick={() => setCreating(!creating)}>
            <i className="fas fa-plus" /> New listing
          </button>
          {creating && (
            <div className="hd-menu">
              {NEW_LISTINGS.map((item) => (
                <button key={item.to} type="button" className="hd-menu-item" onClick={() => startListing(item.to)}>
                  <i className={item.icon} /> {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <nav className="hd-nav">
          {SECTIONS.map((section) => (
            <div key={section.title || "main"} className="hd-nav-section">
              {section.title && <span className="hd-nav-title">{section.title}</span>}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) => `hd-nav-item ${isActive ? "active" : ""}`}
                >
                  <i className={item.icon} />
                  <span>{item.label}</span>
                  {item.badge === "messages" && unreadMessages > 0 && (
                    <span className="hd-badge">{unreadMessages > 99 ? "99+" : unreadMessages}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="hd-sidebar-footer">
          <NavLink to="/app/account" className={({ isActive }) => `hd-nav-item ${isActive ? "active" : ""}`}>
            <i className="far fa-user-circle" />
            <span>Account</span>
          </NavLink>
          <button type="button" className="hd-nav-item" onClick={logout}>
            <i className="fas fa-sign-out-alt" />
            <span>Log out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  unreadMessages: PropTypes.number,
};

export default DashboardSidebar;
